import { Spacer, Text } from '@koalafi/component-library';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../button/Button';
import Link from '../Link';
import styled from '@emotion/styled';
import { colors } from '../baseStyle/Variables';
import { ReactComponent as ThumbUpImage } from '../../assets/thumb_up.svg';
import { ReactComponent as ArrowOutwardImage } from '../../assets/arrow_outward.svg';
import { ReactComponent as BoltImage } from '../../assets/bolt.svg';
import { ReactComponent as ShieldImage } from '../../assets/gpp_good.svg';
import { ReactComponent as RadarImage } from '../../assets/radar.svg';
import { ReactComponent as FastForwardImage } from '../../assets/fast_forward.svg';
import { ReactComponent as PlaidLogoImage } from '../../assets/plaid_logo.svg';
import ManualEntryLink from './manualEntryLink';
import { getDisclosureUrl, DisclosureLink } from '../../constants/disclosure-links';

const SvgList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 7px;
`;
const SvgBullet = styled.div`
  padding-top: 3px;
`;

type PaymentSelectionBaseProps = {
  header: ReactNode;
  subText: ReactNode;
  primaryButtonText: string;
  primaryButtonAction: () => void;
  secondaryButtonAction: () => void;
  isLoading?: boolean;
  plaidRequired?: boolean;
  track: (eventName: string, payload?: object) => void;
  error?: boolean;
};

const PaymentSelectionBase: React.FC<PaymentSelectionBaseProps> = ({
  header,
  subText,
  primaryButtonText,
  primaryButtonAction,
  secondaryButtonAction,
  isLoading = false,
  plaidRequired = false,
  track,
  error = false,
}) => {
  const { t } = useTranslation();

  const disclosureClick = () => {
    track('Plaid Authorization Disclosure');
    window.open(getDisclosureUrl(DisclosureLink.PlaidAuthorization));
  };

  return (
    <div>
      {/* Powered by Plaid - At top */}
      {!error && (
        <>
          <Text size="sm" alignText="left" weight="light">
            <span className="inline-block">{t(($) => $.pagePaymentSelection.poweredBy)}&nbsp;</span>
            <PlaidLogoImage className="inline-block" style={{ marginBottom: '2px' }} />
          </Text>
          <Spacer size="sm" />
        </>
      )}

      {/* Header */}
      <Text size="3xl" alignText="left" weight="extrabold">
        {header}
      </Text>
      <Spacer size="md" />
      <Text size="md" alignText="left" weight="light">
        {subText}
      </Text>
      <Spacer size="sm" />
      {!error ? (
        <>
          {/* Bullet Points - Only shown in long form */}
          <>
            <SvgList>
              <SvgBullet>
                <RadarImage />
              </SvgBullet>
              <Text size="md" alignText="left" weight="light">
                {t(($) => $.pagePaymentSelection.seeBeyondYourCreditScore)}
              </Text>
            </SvgList>
            <Spacer size="xs" />
            <SvgList>
              <SvgBullet>
                <FastForwardImage />
              </SvgBullet>
              <Text size="md" alignText="left" weight="light">
                {t(($) => $.pagePaymentSelection.mostPeopleSkipManualEntry)}
              </Text>
            </SvgList>
            <Spacer size="xs" />
            <SvgList>
              <SvgBullet>
                <BoltImage />
              </SvgBullet>
              <Text size="md" alignText="left" weight="light">
                {t(($) => $.pagePaymentSelection.instantConnection)}
              </Text>
            </SvgList>
            <Spacer size="xs" />
            <SvgList>
              <SvgBullet>
                <ShieldImage />
              </SvgBullet>
              <Text size="md" alignText="left" weight="light">
                {t(($) => $.pagePaymentSelection.securedBy256BitEncryption)}
              </Text>
            </SvgList>
            <Spacer size="sm" />
          </>

          {/* Consent Text */}
          <Text size="sm" alignText="left" weight="light">
            {t(($) => $.pagePaymentSelection.byConnectingWithPlaid)}
            <Link onClick={disclosureClick} linkText={t(($) => $.pagePaymentSelection.plaidAuthorization)} />
          </Text>
          <Spacer size="xs" />

          {/* Primary Button */}
          <Button type="submit" onClick={primaryButtonAction} dataLoading={isLoading}>
            {primaryButtonText}
          </Button>
        </>
      ) : (
        <Button type="submit" onClick={primaryButtonAction} dataLoading={isLoading}>
          {primaryButtonText}
        </Button>
      )}

      {/* The Plaid Required feature flag hides the manual entry button so that customers must use the Plaid flow to enter banking information (OR-1345) 
          Once we validate that this is method of requiring Plaid is effective, we'll look into moving this out of a feature flag and into something
          more permanent, like a setting on a merchant */}
      {!plaidRequired && <ManualEntryLink error={error} secondaryButtonAction={secondaryButtonAction} />}
    </div>
  );
};

export default PaymentSelectionBase;
